import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {AiCreditsPill} from '@Components/ai-credits-pill/ai-credits-pill';
import {CreditsPillSize, CreditsPillTheme} from '@Components/ai-credits-pill/ai-credits-pill.types';
import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {Button, Size, Type} from '@Components/button';
import {openAiChatAuthenticateModal} from '@Libraries/ai-chat.library';
import {isUserPremium, isUserPremiumPlus} from '@Libraries/user.library';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {formatToThousand} from '@Utils/number.util';
import {refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {useAppSelector} from '@/hooks';
import {noop} from '@Utils/general.util';
import {AiChatSuperUserOptions} from '@Components/ai-chat-super-user-options/ai-chat-super-user-options';
import styles from './ai-chat-account-status-bar.module.scss';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';

const CREDITS_LEARN_MORE_URL = 'https://support.postermywall.com/hc/en-us/articles/42437980646285-How-much-does-PosterMyWall-AI-cost';
const CREDITS_DROPDOWN_HOVER_CLOSE_DELAY_MS = 300;

const formatExpiryDate = (isoDate: string): string | null => {
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) {
    return null;
  }
  return d.toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'});
};

export function AiChatAccountStatusBar(): ReactElement {
  const {state, actions} = useAiChat();
  const isPremiumUser = isUserPremium() || isUserPremiumPlus();
  const freeCredits = useAppSelector((s) => s.teamCredits.freeCredits);
  const freeCreditsExpiryDate = useAppSelector((s) => s.teamCredits.freeCreditsExpiryDate);
  const legacyCredits = useAppSelector((s) => s.teamCredits.legacyCredits);
  const legacyCreditsExpiryDate = useAppSelector((s) => s.teamCredits.legacyCreditsExpiryDate);
  const purchasedCredits = useAppSelector((s) => s.teamCredits.purchasedCredits);
  const [isCreditsDropdownOpen, setIsCreditsDropdownOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MOBILE_MODAL_ACTIVATION_BREAKPONT);

  const cancelScheduledClose = (): void => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = (): void => {
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(() => {
      setIsCreditsDropdownOpen(false);
    }, CREDITS_DROPDOWN_HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return (): void => {
      cancelScheduledClose();
    };
  }, []);

  const handleLoginClick = (): void => {
    void openAiChatAuthenticateModal(actions.onLoginSuccessActions);
  };

  const getLoginButton = (): ReactElement | null => {
    if (state.isUserLoggedIn) {
      return null;
    }

    return (
      <Button text={window.i18next.t('pmwjs_user_login_action')} icon={'icon-sign-in'} type={Type.GHOST} customClasses="radius-8" size={Size.SMALL} onClick={handleLoginClick} />
    );
  };

  const getSubscriptionLabel = (): ReactElement => {
    if (isUserPremiumPlus()) {
      return (
        <span className="flex-row-align-center gap-1">
          <Icon icon="icon-crown" size={IconSize.SIZE_ICON_16} className="content-premium" />
          <Text val={window.i18next.t('pmwjs_premium_plus')} size={TextSize.XSMALL} className="content-body" />
        </span>
      );
    }

    if (isUserPremium()) {
      return (
        <span className="flex-row-align-center gap-1">
          <Icon icon="icon-crown" size={IconSize.SIZE_ICON_16} className="content-secondary" />
          <Text val={window.i18next.t('pmwjs_premium')} size={TextSize.XSMALL} className="content-body" />
        </span>
      );
    }

    return <Text val={window.i18next.t('pmwjs_ai_credits_free_plan')} size={TextSize.XSMALL} className="content-body" />;
  };

  const onAddCreditsClick = (): void => {
    setIsCreditsDropdownOpen(false);
    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase(
        () => {
          void refreshTeamCreditsInStore();
        },
        noop,
        undefined,
        window.i18next.t('pmwjs_select_a_credit_pack')
      );
    });
  };

  const onUpgradeClick = (): void => {
    setIsCreditsDropdownOpen(false);
    showPurchasePremiumDialog();
  };

  const onLearnMoreClick = (): void => {
    setIsCreditsDropdownOpen(false);
    window.open(CREDITS_LEARN_MORE_URL, '_blank');
  };

  const getCreditsRow = (): ReactElement => {
    return (
      <div className={styles.creditsInfoRow}>
        <Text val={window.i18next.t('pmwjs_credits')} size={TextSize.XSMALL} className="content-body" />
        <span className="flex-row-align-center gap-1">
          <Icon icon="icon-credits" size={IconSize.SIZE_ICON_16} className="content-body" />
          <Text val={formatToThousand(state.credits)} size={TextSize.XSMALL} className="content-body" />
        </span>
      </div>
    );
  };

  const getSubscriptionRow = (): ReactElement => {
    return (
      <div className={styles.creditsInfoRow}>
        <Text val={window.i18next.t('pmwjs_subscription')} size={TextSize.XSMALL} className="content-body" />
        {getSubscriptionLabel()}
      </div>
    );
  };

  const getCreditsActionButton = (): ReactElement => {
    if (isPremiumUser) {
      return <Button text={window.i18next.t('pmwjs_ai_credits_add_more')} type={Type.PRIMARY} size={Size.SMALL} customClasses="radius-8 flex-1" onClick={onAddCreditsClick} />;
    }

    return (
      <Button
        text={window.i18next.t('pmwjs_ai_credits_upgrade_for_more')}
        type={Type.PREMIUM}
        size={Size.SMALL}
        icon="icon-crown"
        customClasses="radius-8 flex-1"
        onClick={onUpgradeClick}
      />
    );
  };

  const getLearnMoreButton = (): ReactElement => {
    return <Button text={window.i18next.t('pmwjs_ai_credits_learn_how')} type={Type.SECONDARY} size={Size.SMALL} customClasses="radius-8 flex-1" onClick={onLearnMoreClick} />;
  };

  const getCreditsExpiryRows = (): ReactElement | null => {
    if (!window.PMW.creditSplitEnabled) {
      return null;
    }
    const rows: ReactElement[] = [];
    if (freeCredits > 0 && freeCreditsExpiryDate) {
      const freeExpiryFormatted = formatExpiryDate(freeCreditsExpiryDate);
      if (freeExpiryFormatted) {
        rows.push(
          <div key="free" className={styles.creditsInfoRow}>
            <Text
              val={window.i18next.t('pmwjs_x_plan_credits_expire_on_date', {
                numCredits: freeCredits,
                date: freeExpiryFormatted,
              })}
              size={TextSize.XSMALL}
              className="content-body"
            />
          </div>
        );
      }
    }
    if (legacyCredits > 0 && legacyCreditsExpiryDate) {
      const legacyExpiryFormatted = formatExpiryDate(legacyCreditsExpiryDate);
      if (legacyExpiryFormatted) {
        rows.push(
          <div key="legacy" className={styles.creditsInfoRow}>
            <Text
              val={window.i18next.t('pmwjs_x_legacy_credits_expire_on_date', {
                numCredits: legacyCredits,
                date: legacyExpiryFormatted,
              })}
              size={TextSize.XSMALL}
              className="ct-neutral-secondary"
            />
          </div>
        );
      }
    }
    if (purchasedCredits > 0) {
      rows.push(
        <div key="purchased" className={styles.creditsInfoRow}>
          <Text val={window.i18next.t('pmwjs_x_purchased_credits_never_expire', {numCredits: purchasedCredits})} size={TextSize.XSMALL} className="ct-neutral-secondary" />
        </div>
      );
    }
    return rows.length > 0 ? <>{rows}</> : null;
  };

  const getCreditsDropdownPopup = (): ReactElement => {
    const expiryRows = getCreditsExpiryRows();
    return (
      <div className={styles.creditsPopupContainer} onMouseEnter={cancelScheduledClose} onMouseLeave={scheduleClose}>
        <div className={styles.creditsInfoContainer}>
          {getCreditsRow()}
          {getSubscriptionRow()}
        </div>
        <div className={styles.creditsButtonsContainer}>
          {getCreditsActionButton()}
          {getLearnMoreButton()}
        </div>
        {expiryRows !== null ? <div className={styles.creditsExpiryContainer}>{expiryRows}</div> : null}
      </div>
    );
  };

  const getCreditsPillSelector = (): ReactElement => {
    return (
      <div
        onMouseEnter={(): void => {
          cancelScheduledClose();
          setIsCreditsDropdownOpen(true);
        }}
        onMouseLeave={scheduleClose}
      >
        <AiCreditsPill
          credits={state.credits}
          theme={CreditsPillTheme.DEFAULT}
          size={CreditsPillSize.MEDIUM}
          onClick={(): void => {
            setIsCreditsDropdownOpen(!isCreditsDropdownOpen);
          }}
        />
      </div>
    );
  };

  const getCreditsPill = (): ReactElement | null => {
    if (!state.isUserLoggedIn) {
      return null;
    }

    if (!isPremiumUser && !state.isPaygUserWithAiChatAccess) {
      return null;
    }

    if (!isPremiumUser && state.chatHistory.length === 0 && !state.currentChatId) {
      return null;
    }

    return (
      <Dropdown
        open={isCreditsDropdownOpen}
        className={isPremiumButtonNotNeeded() ? 'spacing-m-r-1' : ''}
        selector={getCreditsPillSelector()}
        popup={getCreditsDropdownPopup()}
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_credits')}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        popUpHasCustomWidth
        popupClassName={styles.creditsDropdownPopup}
        onClose={(): void => {
          setIsCreditsDropdownOpen(false);
        }}
      />
    );
  };

  const showPurchasePremiumDialog = (): void => {
    window.PMW.openPremiumPricingModal(false);
  };

  const isPremiumButtonNotNeeded = (): boolean => {
    return state.isUserLoggedIn && isPremiumUser && !state.isSmall;
  };

  const getMobilePremiumButton = (): ReactElement => {
    return (
      <Button
        text={window.i18next.t('pmwjs_upgrade')}
        size={Size.XSMALL}
        type={Type.GHOST}
        textClasses={styles.mobileButtonTextColor}
        iconClassName={styles.mobileButtonTextColor}
        customClasses={'radius-8'}
        icon={'icon-crown'}
        onClick={showPurchasePremiumDialog}
      />
    );
  };
  const getPremiumButton = (): ReactElement | null => {
    if (!state.isUserLoggedIn) {
      return null;
    }

    if (isPremiumUser) {
      return null;
    }

    if (state.isSmall) {
      return getMobilePremiumButton();
    }

    return (
      <Button text={window.i18next.t('pmwjs_upgrade')} icon={'icon-crown'} type={Type.PREMIUM} customClasses="radius-8" size={Size.SMALL} onClick={showPurchasePremiumDialog} />
    );
  };

  const getSuperUserOptions = (): ReactElement | null => {
    if (isMobile) {
      return null;
    }

    return <AiChatSuperUserOptions />;
  };

  if (state.isSmall) {
    return (
      <div className={'flex-row-align-center gap-2'}>
        {getLoginButton()}
        {getPremiumButton()}
        {getCreditsPill()}
        {getSuperUserOptions()}
      </div>
    );
  }

  return (
    <div className={'flex-row-align-center gap-2'}>
      {getLoginButton()}
      {getCreditsPill()}
      {getSuperUserOptions()}
      {getPremiumButton()}
    </div>
  );
}
